import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    Add, CheckCircle, Checklist, ErrorOutline, Groups, Delete, Edit, PersonAdd, Shield, VolunteerActivism
} from '@mui/icons-material';
import { AuthContext } from '../../context/AuthContext';
import Filters from './Filters';
import Loading from '../../components/Loading';
import { simpleDivisionMappings, roles, divisionMappings, admins } from '../../Constants';
import { isTeamOwner, fetchTeamsByOwner } from '../../services/TeamService';
import { fetchLookingForPlayersPosts, createLookingForPlayersPost, updateLookingForPlayersPost, deleteLookingForPlayersPost } from '../../services/LookingForPlayersService';
import { LookingForPlayers } from '../../models/LookingForPlayers';
import { TeamTier } from '../../models/TeamTier';
import { getTeamTiers, isTeamTierTeam } from '../../services/TeamTierService';
import { TeamTierBadge } from '../../components/Badges/TeamTierBadge';
import { Team } from '../../models/Team';
import styles from './LookingForPlayersPage2.module.css';

// "2 Division B" (divisionMappings) → "2. Division B" as in the redesign; "Ligaen" unchanged.
const formatDivision = (name: string | undefined): string | undefined =>
    name?.replace(/^(\d) /, '$1. ');

const teamInitials = (name: string): string =>
    name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase();

// The LFP page, served at /lfp. Replaced the original at the season 33 launch;
// /lfp2 redirects here.
const LookingForPlayersPage2: React.FC = () => {
    const { t } = useTranslation(['pages', 'common']);
    const { user } = useContext(AuthContext);
    const [posts, setPosts] = useState<LookingForPlayers[]>([]);
    const [filteredPosts, setFilteredPosts] = useState<LookingForPlayers[]>([]);
    const [teamTiers, setTeamTiers] = useState<TeamTier[]>([]);
    const [loading, setLoading] = useState(true);
    const [ownedTeams, setOwnedTeams] = useState<Team[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [editingPost, setEditingPost] = useState<LookingForPlayers | null>(null);
    const [selectedTeam, setSelectedTeam] = useState<Team | null>(null);
    const [newPost, setNewPost] = useState({
        title: "",
        minELO: 1500,
        minDivisionRequirements: "",
        roleRequirements: [] as string[],
        requirements: "",
        whatWeOffer: ""
    });
    const [toast, setToast] = useState<{ text: string; error?: boolean } | null>(null);
    const toastTimer = useRef<number | undefined>(undefined);

    const showToast = (text: string, error = false) => {
        if (toastTimer.current) window.clearTimeout(toastTimer.current);
        setToast({ text, error });
        toastTimer.current = window.setTimeout(() => setToast(null), 3200);
    };

    const isUserAdmin = useCallback(() => {
        if (!user) return false;
        return admins.includes(user.steamID);
    }, [user]);

    const fetchPosts = useCallback(async () => {
        setLoading(true);
        try {
            const postsData = await fetchLookingForPlayersPosts();
            setPosts(postsData);
            setFilteredPosts(postsData);
        } catch (error) {
            console.error("Error fetching posts:", error);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleTeamChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const teamId = e.target.value;
        const team = ownedTeams.find(t => t.teamId === teamId);
        setSelectedTeam(team || null);

        if (team) {
            const existingPost = posts.find(post => post.teamId === team.teamId);
            if (existingPost) {
                setEditingPost(existingPost);
                setNewPost({
                    title: existingPost.title,
                    minELO: existingPost.minELO,
                    minDivisionRequirements: existingPost.minDivisionRequirements,
                    roleRequirements: existingPost.roleRequirements,
                    requirements: existingPost.requirements,
                    whatWeOffer: existingPost.whatWeOffer,
                });
            } else {
                setEditingPost(null);
                resetNewPost();
            }
        }
    };

    const fetchOwnedTeams = useCallback(async () => {
        if (user) {
            try {
                const teams = await fetchTeamsByOwner(user.steamID);
                const uniqueTeams = teams.filter((team, index, self) =>
                    index === self.findIndex(t => t.teamId === team.teamId)
                );
                setOwnedTeams(uniqueTeams);

                // Auto-select the first team if only one is available
                if (teams.length === 1) {
                    setSelectedTeam(uniqueTeams[0]);
                }
            } catch (error) {
                console.error("Error fetching owned teams:", error);
            }
        }
    }, [user]);

    useEffect(() => {
        fetchPosts();
        fetchOwnedTeams();
        // Cached 30 min client-side; drives both the top placement and the badge on the cards.
        getTeamTiers().then(setTeamTiers).catch(() => setTeamTiers([]));
    }, [fetchPosts, fetchOwnedTeams]);

    const handleCreatePost = async () => {
        if (!user) return;

        // If admin, allow creating posts even without owning teams
        if (ownedTeams.length === 0 && !isUserAdmin()) {
            // Not a team owner or admin — point them at us instead of the modal
            window.open('https://x.com/realxGuys', '_blank');
            return;
        }

        if (!selectedTeam && !isUserAdmin()) {
            showToast(t('pages:lfp.alertSelectTeam'), true);
            return;
        }

        if (selectedTeam) {
            const existingPost = posts.find(post => post.teamId === selectedTeam.teamId);
            if (existingPost) {
                showToast(t('pages:lfp.alertExistingPost'), true);
                return;
            }
        }

        try {
            if (!isUserAdmin()) {
                const isOwner = await isTeamOwner(user.steamID);
                if (!isOwner) {
                    showToast(t('pages:lfp.alertNotOwner'), true);
                    return;
                }
            }
        } catch (error) {
            console.error("Error checking if user is owner:", error);
        }

        setEditingPost(null);
        resetNewPost();
        setShowModal(true);
    };

    const handleEditPost = async () => {
        if (user && editingPost) {
            const updatedPostData: LookingForPlayers = {
                ...editingPost,
                title: newPost.title,
                minELO: newPost.minELO,
                minDivisionRequirements: newPost.minDivisionRequirements,
                roleRequirements: newPost.roleRequirements,
                requirements: newPost.requirements,
                whatWeOffer: newPost.whatWeOffer,
            };

            try {
                const updatedPost = await updateLookingForPlayersPost(updatedPostData);
                const updatedPosts = posts.map(post => post.id === updatedPost.id ? updatedPost : post);
                setPosts(updatedPosts);
                setFilteredPosts(updatedPosts);
                setShowModal(false);
                setEditingPost(null);
                resetNewPost();
            } catch (error) {
                console.error("Error updating post:", error);
                showToast(t('pages:lfp.saveError'), true);
            }
        }
    };

    const handleSavePost = async () => {
        if (user && selectedTeam) {
            const newPostData: LookingForPlayers = {
                id: 0, // id will be set by the backend upon creation
                title: newPost.title,
                minELO: newPost.minELO,
                minDivisionRequirements: newPost.minDivisionRequirements,
                roleRequirements: newPost.roleRequirements,
                requirements: newPost.requirements,
                whatWeOffer: newPost.whatWeOffer,
                teamId: selectedTeam.teamId,
                teamLogo: selectedTeam.teamLogo,
                teamName: selectedTeam.teamName,
                lastDivision: selectedTeam.division,
                createdAt: new Date()
            };

            try {
                const createdPost = await createLookingForPlayersPost(newPostData);
                setPosts([...posts, createdPost]);
                setFilteredPosts([...posts, createdPost]);
                setShowModal(false);
                resetNewPost();
            } catch (error) {
                console.error("Error creating post:", error);
                showToast(t('pages:lfp.saveError'), true);
            }
        }
    };

    const handleDeletePost = async (postId: number) => {
        const confirmed = window.confirm(t('pages:lfp.confirmDelete'));
        if (confirmed) {
            try {
                const success = await deleteLookingForPlayersPost(postId);
                if (success) {
                    const updatedPosts = posts.filter(post => post.id !== postId);
                    setPosts(updatedPosts);
                    setFilteredPosts(updatedPosts);
                } else {
                    showToast(t('pages:lfp.deleteError'), true);
                }
            } catch (error) {
                console.error("Error deleting post:", error);
                showToast(t('pages:lfp.deleteError'), true);
            }
        }
    };

    const resetNewPost = () => {
        setNewPost({
            title: "",
            minELO: 1500,
            minDivisionRequirements: "",
            roleRequirements: [],
            requirements: "",
            whatWeOffer: ""
        });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        // Number inputs (minELO) must be stored/sent as numbers — the API binds them
        // to Int32, and a string value fails deserialization (400 validation error).
        setNewPost(prev => ({
            ...prev,
            [name]: type === 'number' ? (value === '' ? 0 : Number(value)) : value,
        }));
    };

    const toggleModalRole = (role: string) => {
        setNewPost(prev => ({
            ...prev,
            roleRequirements: prev.roleRequirements.includes(role)
                ? prev.roleRequirements.filter(r => r !== role)
                : [...prev.roleRequirements, role],
        }));
    };

    const isAdmin = isUserAdmin();

    const renderPostCard = (post: LookingForPlayers) => {
        const userOwnsTeam = ownedTeams.some((team) => team.teamId === post.teamId);
        const userCanEditPost = userOwnsTeam || isAdmin;
        const division = formatDivision(divisionMappings.get(post.lastDivision));

        return (
            <div className={styles.postCard} key={post.id}>
                {/* header: team identity + positions open */}
                <div className={styles.postHeader}>
                    {post.teamLogo
                        ? <img src={post.teamLogo} alt="" className={styles.teamLogo} />
                        : <div className={styles.teamInitials}>{teamInitials(post.teamName || '?')}</div>}
                    <div className={styles.teamIdentity}>
                        <Link to={`/team/${post.teamId}`} className={styles.teamName}>
                            {post.teamName}
                            {isTeamTierTeam(post.teamId, teamTiers) && <TeamTierBadge teamName={post.teamName} />}
                        </Link>
                        <div className={styles.teamMeta}>
                            {division && <><span>{division}</span><span className={styles.teamMetaDot}>·</span></>}
                            <span>{t('pages:lfp.minEloShort')} <span className={styles.teamMetaElo}>{post.minELO}</span></span>
                        </div>
                    </div>
                    {post.roleRequirements.length > 0 && (
                        <div className={styles.seekingWrap}>
                            <span className={styles.seekingLabel}>{t('pages:lfp.seeking')}</span>
                            <div className={styles.rolePills}>
                                {post.roleRequirements.map((role) => (
                                    <span className={styles.rolePill} key={role}>
                                        <PersonAdd sx={{ fontSize: 15 }} />
                                        {role}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
                {/* body: the pitch */}
                <div className={styles.postBody}>
                    <div>
                        <div className={styles.sectionLabel}>
                            <VolunteerActivism sx={{ fontSize: 17 }} />
                            {t('pages:lfp.offerSection')}
                        </div>
                        <p className={styles.sectionText}>{post.whatWeOffer || 'N/A'}</p>
                    </div>
                    <div className={styles.postBodyRight}>
                        <div className={styles.sectionLabel}>
                            <Checklist sx={{ fontSize: 17 }} />
                            {t('pages:lfp.requirementsSection')}
                        </div>
                        <p className={styles.sectionText}>{post.requirements || 'N/A'}</p>
                    </div>
                </div>
                {/* footer */}
                <div className={styles.postFooter}>
                    <Link to={`/team/${post.teamId}`} className={styles.footerButton}>
                        <Groups sx={{ fontSize: 17 }} />
                        {t('pages:lfp.viewTeamShort')}
                    </Link>
                    {userCanEditPost && (
                        <>
                            <button
                                className={`${styles.footerButton} ${styles.footerButtonNeutral}`}
                                onClick={() => {
                                    setEditingPost(post);
                                    setNewPost({
                                        title: post.title,
                                        minELO: post.minELO,
                                        minDivisionRequirements: post.minDivisionRequirements,
                                        roleRequirements: post.roleRequirements,
                                        requirements: post.requirements,
                                        whatWeOffer: post.whatWeOffer,
                                    });
                                    setShowModal(true);
                                }}
                            >
                                <Edit sx={{ fontSize: 17 }} />
                                {isAdmin && !userOwnsTeam ? t('pages:lfp.adminEdit') : t('pages:lfp.edit')}
                            </button>
                            <button
                                className={`${styles.footerButton} ${styles.footerButtonDanger}`}
                                onClick={() => handleDeletePost(post.id)}
                            >
                                <Delete sx={{ fontSize: 17 }} />
                                {isAdmin && !userOwnsTeam ? t('pages:lfp.adminDelete') : t('pages:lfp.delete')}
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    };

    const renderPosts = () => {
        if (loading) {
            return (
                <div className="text-center">
                    <Loading />
                </div>
            );
        }
        if (filteredPosts.length === 0) {
            return (
                <p className={styles.emptyState}>
                    {posts.length === 0 ? t('pages:lfp.noPosts') : t('pages:lfp.noMatch')}
                </p>
            );
        }
        return filteredPosts.map(renderPostCard);
    };


    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <div>
                    <h1 className={styles.title}>{t('pages:lfp.title')}</h1>
                    <p className={styles.subtitle}>
                        {t('pages:lfp.subtitle')} · <span className={styles.subtitleCount}>{t('pages:lfp.countPosts', { count: filteredPosts.length })}</span>
                    </p>
                </div>
                <div className={styles.headerActions}>
                    {(ownedTeams.length > 0 || isAdmin) && (
                        <select
                            className={styles.teamSelect}
                            value={selectedTeam?.teamId ?? ""}
                            onChange={handleTeamChange}
                        >
                            <option value="">{t('pages:lfp.selectYourTeam')}</option>
                            {ownedTeams.map((team) => (
                                <option key={team.teamId} value={team.teamId}>
                                    {team.teamName}
                                </option>
                            ))}
                        </select>
                    )}
                    {user && (
                        <button className={styles.ctaButton} onClick={handleCreatePost} title={ownedTeams.length === 0 && !isAdmin ? t('pages:lfp.notOwnerMessage') : undefined}>
                            <Add sx={{ fontSize: 18 }} />
                            {t('pages:lfp.createShort')}
                        </button>
                    )}
                </div>
            </div>

            {isAdmin && (
                <div className={styles.adminBanner}>
                    <Shield sx={{ fontSize: 19 }} />
                    {t('pages:lfp.adminNotice')}
                </div>
            )}

            <div className={styles.body}>
                <Filters posts={posts} setFilteredPosts={setFilteredPosts} teamTiers={teamTiers} />
                <div className={styles.postList}>
                    {renderPosts()}
                </div>
            </div>

            {/* Create/edit modal — portaled to body (the page fadeUp animation would trap position:fixed) */}
            {showModal && createPortal(
                <div className={styles.modalOverlay} onClick={() => setShowModal(false)}>
                    <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
                        <h2 className={styles.modalTitle}>
                            {editingPost ? t('pages:lfp.editModal') : t('pages:lfp.createModal')}
                        </h2>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>{t('pages:lfp.titleField')}</label>
                            <input
                                className={styles.input}
                                type="text"
                                name="title"
                                value={newPost.title}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className={`${styles.fieldGroup} ${styles.fieldRow}`}>
                            <div>
                                <label className={styles.fieldLabel}>{t('pages:lfp.minELO')}</label>
                                <input
                                    className={styles.input}
                                    type="number"
                                    name="minELO"
                                    value={newPost.minELO}
                                    onChange={handleInputChange}
                                />
                            </div>
                            <div>
                                <label className={styles.fieldLabel}>{t('pages:lfp.minDivision')}</label>
                                <select
                                    className={styles.select}
                                    name="minDivisionRequirements"
                                    value={newPost.minDivisionRequirements}
                                    onChange={handleInputChange}
                                >
                                    {Array.from(simpleDivisionMappings.entries()).map(([key, value]) => (
                                        <option key={key} value={key}>{formatDivision(value)}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>{t('pages:lfp.rolesWanted')}</label>
                            <div className={styles.chipRow} style={{ marginBottom: 0 }}>
                                {roles.map((role) => (
                                    <button
                                        key={role}
                                        type="button"
                                        className={`${styles.chip} ${newPost.roleRequirements.includes(role) ? styles.chipActive : ''}`}
                                        onClick={() => toggleModalRole(role)}
                                    >
                                        {role}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>{t('pages:lfp.offerSection')}</label>
                            <textarea
                                className={styles.textarea}
                                name="whatWeOffer"
                                value={newPost.whatWeOffer}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>{t('pages:lfp.requirementsSection')}</label>
                            <textarea
                                className={styles.textarea}
                                name="requirements"
                                value={newPost.requirements}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.cancelButton} onClick={() => setShowModal(false)}>
                                {t('common:buttons.close')}
                            </button>
                            <button className={styles.ctaButton} onClick={editingPost ? handleEditPost : handleSavePost}>
                                {editingPost ? t('pages:lfp.saveChanges') : t('pages:lfp.savePost')}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Toast — portaled for the same reason */}
            {toast && createPortal(
                <div className={`${styles.toast} ${toast.error ? styles.toastError : ''}`}>
                    {toast.error
                        ? <ErrorOutline className={styles.toastIconError} sx={{ fontSize: 20 }} />
                        : <CheckCircle className={styles.toastIconOk} sx={{ fontSize: 20 }} />}
                    <span className={styles.toastText}>{toast.text}</span>
                </div>,
                document.body
            )}
        </div>
    );
};

export default LookingForPlayersPage2;
